/**
 * Chat pipeline — orchestration glue between Composer, Send_Queue,
 * Model_Router executor, Prompt_Builder, Token_Budgeter, the Gemini
 * client, the Naming_Flow reducer, and the Rolling_Summarizer.
 *
 * This module owns the end-to-end "user typed something, render the
 * assistant's reply" flow (Task 23.1). It is intentionally a thin
 * coordinator: every domain rule (prompt composition, fallback
 * decisions, summary trigger, naming-flow classification) lives in a
 * pure module under `src/domain/`. The pipeline only:
 *
 *   1. Appends the user bubble immediately for sub-50 ms perceived
 *      send latency (Requirement 11.2).
 *   2. Runs the Naming_Flow user-reply classifier when the flow is
 *      mid-conversation (Requirements 16.4, 16.5, 16.6, 16.12).
 *   3. Builds the prompt off a snapshot of every relevant slice via
 *      `buildPrompt` (Requirements 4.4, 4.5).
 *   4. Drives the Model_Router executor's async generator and patches
 *      the assistant message in-place on every chunk so streaming
 *      tokens render within 100 ms of arrival (Requirement 11.3).
 *   5. Handles the executor's `streamReplaced` event — the streamed
 *      partial is overwritten by the non-streaming retry's full
 *      reply (Requirement 5.9 / Property 15).
 *   6. Maps a terminal `softFallback` to a localized friendly bubble
 *      with status `softFallback` so the existing "try again"
 *      affordance kicks in (Requirements 5.6, 5.7, 12.3).
 *   7. After every successful turn, looks for a proposed name in the
 *      assistant text and dispatches `aiProposed` so the Naming_Flow
 *      suggested-name set grows monotonically (Requirements 16.2,
 *      16.5; Property 33).
 *   8. Triggers the rolling summarizer when `shouldSummarize` is true
 *      after the turn (Requirement 4.6).
 *   9. Refreshes the diagnostics store so the developer view's
 *      cooldowns / log entries reflect the just-completed call
 *      (Requirements 5.11, 13.3).
 *
 * The pipeline NEVER throws into React. The Model_Router executor is
 * documented to never throw, and every other surface is wrapped in a
 * try/catch that demotes failures to a `failed` message status.
 *
 * Validates: Requirements 4.4, 4.5, 4.6, 5.6, 5.7, 5.8, 5.9, 9.7,
 * 11.2, 11.3, 12.3, 12.4, 16.1, 16.2, 16.4, 16.5, 16.6, 16.12, 5.11,
 * 13.3.
 */

import type { ModelRouterExecutor } from '../domain/modelRouter/executor';
import { buildPrompt } from '../domain/promptBuilder';
import type { GeminiContent, GeminiPart } from '../domain/promptBuilder';
import { shouldSummarize, summarize } from '../domain/summarizer';
import { detectRenameIntent } from '../domain/namingFlow/matcher';
import { clearPersistedHealth } from '../domain/modelRouter/persistence';
import type {
  LanguageCode,
  Message,
  MessageStatus,
} from '../domain/types';
import {
  useCompanionNameStore,
  useDiagnosticsStore,
  useNamingFlowStore,
  usePreferencesStore,
  useProfileStore,
  useThreadsStore,
} from '../state';

/** Localized soft-fallback copy keyed by the active conversation language. */
const SOFT_FALLBACK_TEXT: Readonly<Record<LanguageCode, string>> = {
  tr: 'Şu an yanıt alamadım — biraz sonra tekrar dene. 🔄',
  en: "Couldn't get a response right now — please try again in a moment. 🔄",
  el: 'Δεν μπόρεσα να λάβω απάντηση τώρα — δοκίμασε ξανά σε λίγο. 🔄',
};

/** Localized "something went wrong" copy keyed by language. */
const FAILED_FALLBACK_TEXT: Readonly<Record<LanguageCode, string>> = {
  tr: 'Bir şeyler ters gitti.',
  en: 'Something went wrong.',
  el: 'Κάτι πήγε στραβά.',
};

/** Maximum length of an extracted proposed name (mirrors matcher.ts cap). */
const MAX_NAME_LEN = 30;

/**
 * Best-effort regex set for extracting an AI-proposed companion name
 * from a free-form assistant reply during Naming_Flow. The patterns
 * are intentionally narrow — they target the natural phrasings the AI
 * is instructed to use in `renderIdentityBlock` ("propose exactly ONE
 * soft, feminine, warm name…"), and never override names the End_User
 * supplied via `userProvidesCustom` (which goes through the
 * Naming_Flow classifier directly).
 *
 * Per Requirement 1.9 / 16.3 we MUST NOT hardcode any candidate name
 * literals here. Each pattern captures a name token via a Unicode
 * letter class, never a literal alternation.
 *
 * Patterns are tried in priority order; the first capture wins.
 */
const NAME_PATTERNS: readonly RegExp[] = [
  // Turkish: "Adım Mira", "Bana Mira de", "ismi Mira"
  /(?:adım|ismim|bana|sana)\s+([\p{Lu}\p{Lt}][\p{L}\p{M}\-']{1,29})\b/u,
  /\b([\p{Lu}\p{Lt}][\p{L}\p{M}\-']{1,29})\s+(?:ismi|adı|olabilir|olsun)/u,
  // English: "I'd like to be Mira", "Call me Mira", "How about Mira"
  /(?:i(?:'d| would)\s+like\s+to\s+be(?:\s+called)?|call\s+me|how\s+about|let'?s\s+go\s+with|my\s+name\s+(?:could\s+be|is))\s+([\p{Lu}\p{Lt}][\p{L}\p{M}\-']{1,29})\b/iu,
  // Greek
  /(?:με\s+λένε|λέγομαι|να\s+είμαι(?:\s+η)?)\s+([\p{Lu}\p{Lt}][\p{L}\p{M}\-']{1,29})\b/iu,
  // Generic: capitalized token in quotes
  /["\u201c\u201d\u2018\u2019']([\p{Lu}\p{Lt}][\p{L}\p{M}\-']{1,29})["\u201c\u201d\u2018\u2019']/u,
  // "oner: Mira" / "isim: Mira"
  /(?:önerim?|isim|ad)\s*[:\-]\s*([\p{Lu}\p{Lt}][\p{L}\p{M}\-']{1,29})\b/iu,
  // "**Mira**" bold markdown
  /\*\*([\p{Lu}\p{Lt}][\p{L}\p{M}\-']{1,29})\*\*/u,
];

/**
 * Try to extract a proposed name from `text`. Returns the first
 * pattern's capture group, normalized (trimmed, surrounding
 * punctuation stripped), or `null` when no pattern matches.
 *
 * The function is pure and exported only for unit-test access; the
 * pipeline calls it directly.
 */
export function extractProposedName(text: string): string | null {
  for (const pattern of NAME_PATTERNS) {
    const match = pattern.exec(text);
    if (match === null) continue;
    const captured = match[1];
    if (captured === undefined) continue;
    const cleaned = captured.trim().replace(/[!?.,;:]+$/, '').trim();
    if (cleaned.length === 0 || cleaned.length > MAX_NAME_LEN) continue;
    return cleaned;
  }
  return null;
}

/** Constructor dependencies for {@link createChatPipeline}. */
export interface ChatPipelineDeps {
  executor: ModelRouterExecutor;
}

/** Public surface of a wired pipeline. */
export interface ChatPipeline {
  /**
   * Run one user turn end-to-end. Resolves when the assistant
   * message has been finalized (or marked `failed` / `softFallback`)
   * and the post-turn rolling-summary check has settled.
   *
   * Never throws. UI errors are surfaced via the assistant message's
   * `status` field, not via rejected promises.
   */
  send(
    userText: string,
    opts?: {
      useWebSearch?: boolean;
      /** Optional image to attach to this message. */
      imageData?: { mimeType: string; base64: string; fileName: string } | null;
    },
  ): Promise<void>;
}

/**
 * Build a pipeline bound to the given Model_Router executor. The
 * pipeline is safe to construct once at boot and reuse across every
 * subsequent send — Zustand store reads are deferred to call-time so
 * the pipeline always sees the latest profile, preferences, threads,
 * and naming-flow state.
 */
export function createChatPipeline(deps: ChatPipelineDeps): ChatPipeline {
  const { executor } = deps;

  /**
   * Read the names already proposed in the current Naming_Flow so the
   * Prompt_Builder can forbid the AI from repeating them
   * (Requirement 16.5).
   */
  const readSuggestedNames = (): readonly string[] => {
    const phase = useNamingFlowStore.getState().flowState;
    if (
      phase.phase === 'awaitingFirstReply' ||
      phase.phase === 'awaitingUserResponse' ||
      phase.phase === 'clarifying'
    ) {
      return phase.suggested;
    }
    return [];
  };

  /**
   * Derive `namingFlowActive` from the reducer's phase rather than
   * the slice's `isActive` flag. The flag is updated AFTER the
   * persistence-side `setName` await resolves, so directly after
   * `ingestUserReply(userAccepts)` it can briefly disagree with the
   * synchronously-updated phase. The phase is the canonical source.
   */
  const isFlowActive = (): boolean => {
    const phase = useNamingFlowStore.getState().flowState.phase;
    return phase !== 'idle' && phase !== 'confirmed';
  };

  const send: ChatPipeline['send'] = async (userText, opts) => {
    const useWebSearch = opts?.useWebSearch === true;
    const imageData = opts?.imageData ?? null;
    const threadsStore = useThreadsStore.getState();
    const activeThread = threadsStore.activeThread;
    if (activeThread === null) return;

    const language = usePreferencesStore.getState().preferences.language;

    // 1. Append user message immediately (Requirement 11.2).
    const userMsg: Message = {
      id: crypto.randomUUID(),
      threadId: activeThread.id,
      role: 'user',
      content: userText,
      createdAt: Date.now(),
      status: 'sent',
      summarized: false,
      language,
      ...(imageData !== null ? { imageData } : {}),
    };
    await threadsStore.appendMessage(userMsg);

    // 2. If naming flow is active, classify the user reply BEFORE
    //    building the prompt — `userAccepts` / `userProvidesCustom`
    //    transitions the reducer to `confirmed` synchronously, so
    //    the prompt sees `namingFlowActive: false` and the freshly
    //    persisted companion name on the very next prompt build
    //    (Requirements 16.4, 16.5, 16.6, 16.12).
    //
    //    We DO NOT classify when the flow is in `awaitingFirstReply`
    //    — there is no proposal to accept yet, so any user reply
    //    would be ambiguous by definition. The first AI turn
    //    populates the proposal via `aiProposed` below.
    {
      const namingStore = useNamingFlowStore.getState();
      const phase = namingStore.flowState.phase;
      if (phase === 'awaitingUserResponse' || phase === 'clarifying') {
        namingStore.ingestUserReply(userText, language);
      }
    }

    // 2b. Outside the naming flow (idle / confirmed), detect a rename
    //     intent in the user's message and apply it immediately.
    //     If a rename is detected, update the name and let the AI
    //     reply naturally — the updated name will already be in the
    //     system prompt for this turn.
    let renamedTo: string | null = null;
    {
      const namingPhase = useNamingFlowStore.getState().flowState.phase;
      if (namingPhase === 'idle' || namingPhase === 'confirmed') {
        const newName = detectRenameIntent(userText, language);
        if (newName !== null) {
          renamedTo = newName;
          await useCompanionNameStore.getState().setName(newName);
        }
      }
    }

    // 3. Build the prompt from a fresh snapshot of every slice. The
    //    user message is already in `thread.messages`, so we pass an
    //    empty `pendingUserMessage` to avoid double-counting.
    const refreshedThreadsStore = useThreadsStore.getState();
    const threadForPrompt = refreshedThreadsStore.activeThread;
    if (threadForPrompt === null) return;

    const profile = useProfileStore.getState().profile;
    const prefs = usePreferencesStore.getState().preferences;
    const companionName = useCompanionNameStore.getState().name;
    const namingFlowActive = isFlowActive();

    const built = buildPrompt({
      profile,
      companionName,
      language,
      practiceMode: prefs.practiceMode,
      pacing: prefs.pacing,
      thread: threadForPrompt,
      rollingSummary: threadForPrompt.rollingSummary,
      tokenBudget: prefs.tokenBudget,
      pendingUserMessage: '',
      namingFlowActive,
      alreadySuggestedNames: readSuggestedNames(),
      currentDate: new Date().toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
      }),
    });

    // If the user attached an image, append a multimodal user turn.
    // buildPrompt already placed the text-only history; we add the
    // current turn manually so the image is included in the request.
    // If a rename was detected, replace the raw command with a natural
    // phrasing so safety filters don't block it.
    const finalContents: GeminiContent[] = [...built.contents];
    {
      const parts: GeminiPart[] = [];
      if (imageData !== null) {
        parts.push({ inlineData: { mimeType: imageData.mimeType, data: imageData.base64 } });
      }
      // Use a natural phrasing for rename commands to avoid safety blocks.
      const textForAI = renamedTo !== null
        ? (language === 'tr'
            ? `Artık sana ${renamedTo} diyeceğim.`
            : language === 'el'
              ? `Από τώρα θα σε λέω ${renamedTo}.`
              : `I'll call you ${renamedTo} from now on.`)
        : userText;
      if (textForAI.trim().length > 0) {
        parts.push({ text: textForAI });
      }
      if (parts.length > 0) {
        finalContents.push({ role: 'user', parts });
      }
    }

    // 4. Insert the streaming-placeholder assistant bubble. The
    //    pipeline patches `content` and `status` in place as chunks
    //    arrive (Requirement 11.3).
    const assistantId = crypto.randomUUID();
    const assistantMsg: Message = {
      id: assistantId,
      threadId: threadForPrompt.id,
      role: 'assistant',
      content: '',
      createdAt: Date.now(),
      status: 'streaming',
      summarized: false,
      language,
    };
    await refreshedThreadsStore.appendMessage(assistantMsg);

    // 5. Drive the executor's generator. Errors are demoted to a
    //    `failed` status — the executor itself is documented to
    //    never throw, but defense-in-depth keeps the UI in a known
    //    state when an injected fake misbehaves (Requirement 5.6).
    let accumulated = '';
    let terminalReached = false;
    try {
      const stream = executor.execute({
        systemPrompt: built.systemPrompt,
        contents: finalContents,
        stream: true,
        useWebSearch,
      });
      for await (const event of stream) {
        if (event.kind === 'chunk') {
          if (event.text === undefined) continue;
          accumulated += event.text;
          await useThreadsStore
            .getState()
            .updateMessage(assistantId, {
              content: accumulated,
              status: 'streaming',
            });
        } else if (event.kind === 'streamReplaced') {
          if (event.fullText === undefined) continue;
          accumulated = event.fullText;
          // Build a `Partial<Message>` that omits `modelUsed` when
          // it is `undefined`, satisfying `exactOptionalPropertyTypes`.
          const patch: Partial<Message> =
            event.modelUsed !== undefined
              ? {
                  content: accumulated,
                  status: 'complete' satisfies MessageStatus,
                  modelUsed: event.modelUsed,
                }
              : {
                  content: accumulated,
                  status: 'complete' satisfies MessageStatus,
                };
          await useThreadsStore.getState().updateMessage(assistantId, patch);
          terminalReached = true;
        } else if (event.kind === 'final') {
          if (event.finalText === undefined) continue;
          accumulated = event.finalText;
          const patch: Partial<Message> =
            event.modelUsed !== undefined
              ? {
                  content: accumulated,
                  status: 'complete' satisfies MessageStatus,
                  modelUsed: event.modelUsed,
                }
              : {
                  content: accumulated,
                  status: 'complete' satisfies MessageStatus,
                };
          await useThreadsStore.getState().updateMessage(assistantId, patch);
          terminalReached = true;
        } else if (event.kind === 'softFallback') {
          accumulated = SOFT_FALLBACK_TEXT[language];
          await useThreadsStore.getState().updateMessage(assistantId, {
            content: accumulated,
            status: 'softFallback',
          });
          // Clear persisted health counters so stale rate-limit data
          // doesn't permanently block future requests. The next send
          // will start with a clean slate.
          clearPersistedHealth();
          terminalReached = true;
        }
      }
      // Generator exited without a terminal event — treat as failure
      // so the user sees the inline "try again" affordance.
      if (!terminalReached) {
        await useThreadsStore.getState().updateMessage(assistantId, {
          content:
            accumulated.length > 0 ? accumulated : FAILED_FALLBACK_TEXT[language],
          status: 'failed',
        });
      }
    } catch {
      await useThreadsStore.getState().updateMessage(assistantId, {
        content:
          accumulated.length > 0 ? accumulated : FAILED_FALLBACK_TEXT[language],
        status: 'failed',
      });
    }

    // 6. Refresh diagnostics so the developer view sees the cooldowns
    //    and log entries from this turn (Requirements 5.11, 13.3).
    void useDiagnosticsStore.getState().refresh();

    // 7. Naming_Flow: if the flow is still active and the assistant
    //    actually produced text, look for a proposed name and grow
    //    the suggested set (Requirements 16.2, 16.5; Property 33).
    if (isFlowActive() && accumulated.length > 0) {
      const proposed = extractProposedName(accumulated);
      if (proposed !== null) {
        useNamingFlowStore.getState().aiProposed(proposed);
      }
    }

    // 8. Rolling summary trigger (Requirement 4.6). Re-read the
    //    thread because `appendMessage` / `updateMessage` mutate the
    //    store after the executor's events resolve.
    const postTurnThread = useThreadsStore.getState().activeThread;
    if (
      postTurnThread !== null &&
      shouldSummarize(postTurnThread, prefs.tokenBudget)
    ) {
      try {
        const result = await summarize(
          {
            thread: postTurnThread,
            language,
            budgetTokens: prefs.tokenBudget,
          },
          { oneShot: executor.oneShot.bind(executor) },
        );
        if (
          result.summary !== null &&
          result.summarizedThroughMessageId !== null
        ) {
          await useThreadsStore
            .getState()
            .setRollingSummary(
              result.summary,
              result.summarizedThroughMessageId,
            );
        }
      } catch {
        // Summarization failures are non-fatal — the rolling summary
        // remains intact and the next turn will retry the trigger.
      }
    }
  };

  return {
    send,
  };
}

